import React, { FC, useEffect, useMemo, useState } from 'react';
import { parse, print } from 'graphql';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import debounce from 'lodash/debounce';

import {
  hasRole,
  formatDate,
  userRolesMap,
  getAxiosWithAuthorization,
  useDeepCompareMemo,
  useDeepCompareEffect,
} from '@clatter/platform';
import { config } from '../../../../config';
import { addNotification, authSelectors } from '../../../../store';
import { useGetProjectTemplateByIdQuery } from '../../../../services';
import { gridFilteredSortedRowEntriesSelector, GridLogicOperator, useGridApiRef } from '@mui/x-data-grid-premium';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { fullFormats } from 'ajv-formats/dist/formats';

import { buttonVariants } from '@clatter/ui';
import { aclRolesMap } from '../../../../constants';
import { ProjectPageLayout } from '../../../../layouts';
import { useGetCCMSTypesQuery } from '../../../../services/ccms.service';
import { augmentEntityWithClientName, parseSearchInput } from '../../../../helpers';
import {
  EntitiesManager,
  NgBreadcrumbs,
  NgCard,
  NgCardContent,
  NgCardHeader,
  SearchInput,
  QueryOutputTable,
  NgButton,
} from '../../../../components';
import { GenerateDocumentsSection } from '../components/GenerateDocumentsSection';
import { MicrositesPublishSection } from '../components/MicrositesPublishSection';

interface EntitiesPageProps {
  projectData: any;
  grants: UserGrants;
  isLoading?: boolean;
}

interface GetGraphqlQueryString {
  parsedQuery: ParsedQuery;
  entity: string;
  pageSize?: number;
  primaryKey: string;
}

type WorkspaceFormValues = {
  selectedEntities: any[];
  selectedDocumentTemplates: GenerateDocumentTemplateItem[];
};

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const StyledErrorMessages = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  h4 {
    font-weight: bold;
    color: red;
  }

  li {
    //color: red;
    padding: 1rem 2rem;
    background-color: #ffa7a7;
    margin-bottom: 0.3rem;

    p {
      margin: 0;
    }
  }
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  align-items: start;
  gap: ${({ theme }) => theme.spacing.medium};

  > * {
    min-width: 0;
  }

  .column {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.medium};
    min-width: 0;
  }

  @media (max-width: 1600px) {
    grid-template-columns: 1fr;
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const extractEntityFromQueryString = (query: string): string => {
  const match = query.match(/\{\s*context:\s*(\w+)/);
  return match?.[1] || '';
};

const getInitialData = (workspaceTemplateData: any) => {
  const queries: WorkspaceQuery[] =
    (workspaceTemplateData?.DocumentTemplates || []).reduce((acc: WorkspaceQuery[], current: any) => {
      const queryID = current?.QueryID;
      const query = current?.Query;

      if (queryID && !acc.some((item) => item?.ID === queryID) && query) {
        acc.push(query);
      }

      return acc;
    }, []) || [];

  return {
    queries: queries,
    values: null,
  };
};

const getQueryStringFromParsedResult = ({
  parsedQuery,
  entity,
  primaryKey,
  pageSize = 1000,
}: GetGraphqlQueryString) => {
  // ids - primaryKey values to search
  // names - fieldToCheckNames values to search
  const { primaryKeyValues, names } = parsedQuery || {};
  const fieldToCheckNames = 'CLIENT';

  const filters: string[] = [];

  if (primaryKeyValues && primaryKeyValues?.length > 0) {
    const idsList = `[${(primaryKeyValues || []).map((value) => `"${value}"`).join(', ')}]`;
    filters.push(`{ ${primaryKey}: { in: ${idsList} } }`);
  }

  if (names && names?.length > 0) {
    names.forEach((name) => {
      filters.push(`{ ${fieldToCheckNames}: { regex: ".*${name}.*" } }`);
    });
  }

  let graphqlQuery = `query checkData {
      ${entity}{
        ${primaryKey}
        ${fieldToCheckNames}
      }
    }`;

  if (filters.length > 0) {
    graphqlQuery = `
      query checkData {
        ${entity}(
          page: { size: ${pageSize} }
          filter: {
            _or: [
              ${filters.join(',\n')}
            ]
          }
        ) {
          ${primaryKey}
          ${fieldToCheckNames}
        }
      }
    `;
  }

  // Build the query string (AST format)
  const ast = parse(graphqlQuery);

  return print(ast);
};

type PrimaryKeyMap = {
  key: string;
  types: any;
};

type SchemaField = {
  label: string;
  order: number;
  name: string;
};

type TypeItem = {
  Name: string;
  Schema: Record<string, SchemaField | string>; // fieldSchema can be object or string
};

const validation = {
  type: 'object',
  properties: {
    selectedEntities: {
      type: 'array',
      minLength: 1,
      errorMessage: {
        minLength: 'Selected entities list can not be empty.',
      },
    },
    selectedDocumentTemplates: {
      type: 'array',
      minLength: 1,
      errorMessage: {
        minLength: 'Selected document templates can not be empty.',
      },
    },
  },
  required: ['selectedEntities', 'selectedDocumentTemplates'],
};

const StyledNoDataMessage = styled.div`
  display: flex;
  width: 100%;
  min-height: 300px;
  justify-content: center;
  align-items: center;

  font-size: ${({ theme }) => theme.fontSize.default};
`;

const getComputedTypesData = (
  types: TypeItem[],
): { primaryKeys: PrimaryKeyMap[]; columnsByTypes: Record<string, SchemaField[]> } => {
  const resultMap = new Map<string, Set<string>>();
  const columnsByTypes: Record<string, SchemaField[]> = {};

  types.forEach((type) => {
    const schema = type.Schema;

    const orderedColumns = Object.entries(schema)
      .map(([key, fieldSchema]) => {
        if (fieldSchema && typeof fieldSchema === 'object' && 'order' in fieldSchema) {
          return { name: key, field: key, order: fieldSchema.order };
        }
        return null;
      })
      .filter((col): any => col !== null)
      //@ts-ignore
      .sort((a, b) => a.order - b.order);

    //@ts-ignore
    columnsByTypes[type.Name] = orderedColumns;

    //@ts-ignore
    const primaryKey = orderedColumns.length > 0 ? orderedColumns[0].name : null;

    if (primaryKey) {
      if (!resultMap.has(primaryKey)) {
        resultMap.set(primaryKey, new Set());
      }
      resultMap.get(primaryKey)!.add(type.Name);
    }
  });

  const primaryKeys = Array.from(resultMap.entries()).map(([key, typeNameSet]) => ({
    key,
    types: Array.from(typeNameSet),
  }));

  return { primaryKeys, columnsByTypes };
};

const NoDataMessage: FC<{ grants: UserGrants }> = ({ grants }) => {
  const userData = useSelector(authSelectors.getUserAuthState);

  const isAdmin = hasRole(userRolesMap.clatterAdmin, userData.user) || hasRole(userRolesMap.ngAdmin, userData.user);

  //@todo - project owner (not ready in api)
  const message = useDeepCompareMemo(() => {
    if (!isAdmin || !grants || grants?.role !== aclRolesMap.DATA_ADMIN) {
      return 'There is no data loaded. Please check with your Administrator to ask about loading data.';
    }

    // return 'There is no data loaded. Please create some entities interactively or upload a workbook.';
    return 'No data loaded yet.';
  }, [grants, isAdmin]);

  return (
    <StyledNoDataMessage>
      <p>{message}</p>
    </StyledNoDataMessage>
  );
};

export const DataPage: FC<EntitiesPageProps> = ({ grants, projectData, isLoading: isParentLoading }) => {
  const dispatch = useDispatch();

  const apiRef = useGridApiRef();

  const [searchValue, setSearchValue] = useState('');
  const [sourceFilteredRowCount, setSourceFilteredRowCount] = useState(0);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [selectedType, setSelectedType] = useState<any | null>(null);
  const [queriesResultsMap, setQueriesResultsMap] = useState<WorkspaceQueryInputResults | null>(null);

  const { data: workspaceTemplateData, isLoading: isWorkspaceTemplateDataLoading } = useGetProjectTemplateByIdQuery(
    {
      workspaceId: projectData?.ProjectTemplate?.ID,
    },
    { skip: !projectData?.ProjectTemplate?.ID },
  );

  const { data: typesData, isFetching: isTypesDataLoading } = useGetCCMSTypesQuery(
    { url: `/cms-scopes/${projectData?.CmsScope?.ID}/types` },
    {
      skip: !projectData?.CmsScope?.ID,
      refetchOnMountOrArgChange: true,
    },
  );

  const formMethods = useForm<WorkspaceFormValues>({
    mode: 'onChange',
    //@ts-ignore
    resolver: ajvResolver(validation, {
      formats: fullFormats,
    }),
    defaultValues: {
      selectedEntities: [],
      selectedDocumentTemplates: [],
    },
  });

  const { control, setValue, watch } = formMethods;

  const selectedEntities = watch('selectedEntities') || [];

  const sourceSheet = useDeepCompareMemo(() => {
    return workspaceTemplateData?.Features?.dataBrowserSourceSheet;
  }, [workspaceTemplateData]);

  const sourceBreadcrumbs = useDeepCompareMemo(() => {
    const breadcrumbs = [
      {
        id: '',
        name: 'Source',
      },
    ];

    if (selectedType && !sourceSheet) {
      breadcrumbs.push({
        id: selectedType?.ID,
        name: selectedType?.DisplayName,
      });
    }

    return breadcrumbs;
  }, [selectedType, sourceSheet]);

  const pageBreadcrumbs = useDeepCompareMemo(() => {
    const lastUpdated = selectedType?.LastUpdated || typesData?.[0]?.LastUpdated;
    const updatedString = lastUpdated ? `(Last Updated ${formatDate(lastUpdated)})` : '';
    return [{ id: 1, name: `Data ${updatedString}` }];
  }, [selectedType, typesData]);

  const isLoading = isParentLoading || isWorkspaceTemplateDataLoading || isFetchingData || isTypesDataLoading;

  const computedTypesData = useDeepCompareMemo(() => getComputedTypesData(typesData || []), [typesData]);
  const initialData = useDeepCompareMemo(() => getInitialData(workspaceTemplateData), [workspaceTemplateData]);
  const entityPrimaryKey = useDeepCompareMemo(() => {
    const primaryKey = computedTypesData?.primaryKeys?.[0]?.key;

    if (!primaryKey) {
      console.warn('No primary key found in the type schemas! Defaulting to POLICY');
      return 'POLICY';
    }

    if (computedTypesData?.primaryKeys?.length > 1) {
      console.warn(`Multiple primary keys found in the type schemas. Defaulting to the first one: ${primaryKey}.`);
      return primaryKey;
    }

    return primaryKey;
  }, [computedTypesData]);

  const selectedEntitiesSchema = useMemo(
    () => ({
      type: 'array',
      items: {
        type: 'object',
        properties: {
          [entityPrimaryKey]: {
            type: 'string',
            minLength: 1,
          },
          CLIENT: {
            type: 'string',
            minLength: 1,
          },
          CLIENT_DISPLAY_NAME: {
            type: 'string',
            minLength: 1,
          },
        },
        required: [entityPrimaryKey, 'CLIENT', 'CLIENT_DISPLAY_NAME'],
      },
    }),
    [entityPrimaryKey],
  );

  const handleBreadcrumbClick = () => {
    setSelectedType(null);
  };

  const handleEntitiesManagerChange = (action: any) => {
    switch (action.type) {
      case 'SET_TYPE':
        setSelectedType(action.payload.type);
        break;

      case 'ADD':
        setValue(
          'selectedEntities',
          [
            //@ts-ignore
            augmentEntityWithClientName(action.payload.entity, entityPrimaryKey),
            ...selectedEntities,
          ],
          { shouldDirty: true, shouldTouch: true, shouldValidate: true },
        );
        break;

      default:
        console.log('unsupported action type');
    }
  };

  useEffect(() => {
    if (!sourceSheet) {
      return;
    }

    setSelectedType({ Name: sourceSheet });
  }, [sourceSheet]);

  const handleCheckQueriesForSelectedEntities = async (selected: any[], project: any) => {
    const graphqlUrl = project?.ID ? `${config.assetServiceBaseUrl}/v2/projects/${projectData?.ID}/graphql` : null;

    if (!graphqlUrl) {
      const message = 'Could not perform query: no query url!';
      console.warn(message);
      return;
    }

    const parsedQuery = { primaryKeyValues: selected.map((item: any) => item[entityPrimaryKey]), names: [] };

    // if there are no values to query - skip
    if (!(parsedQuery?.primaryKeyValues?.length > 0)) {
      setQueriesResultsMap(null);
      return;
    }

    setIsFetchingData(true);

    try {
      const queriesToCheck = (initialData?.queries || []).map(async (query: any) => {
        const entity = extractEntityFromQueryString(query.Query);

        const queryString = getQueryStringFromParsedResult({
          pageSize: selected?.length,
          parsedQuery,
          primaryKey: entityPrimaryKey,
          entity,
        });

        if (!queryString) {
          console.warn('Could not perform query: no query string!');
          return null;
        }

        const data = await getAxiosWithAuthorization({
          url: graphqlUrl,
          method: 'POST',
          data: { query: queryString },
        });

        const notFoundKeys = (parsedQuery.primaryKeyValues || []).filter((value: string) => {
          const found = data?.data?.[entity]?.some((item: any) => item[entityPrimaryKey] === value);
          return !found;
        });

        return {
          queryID: query?.ID,
          hasData: data?.data?.[entity]?.length > 0,
          data: data?.data?.[entity],
          params: parsedQuery,
          notFoundKeys: notFoundKeys,
        };
      });

      const queriesFetchResults: any = await Promise.all(queriesToCheck);

      const mappedObject = (queriesFetchResults || []).reduce((acc: any, item: any) => {
        const { queryID, ...rest } = item || {};
        if (queryID) {
          acc[queryID] = rest;
        }
        return acc;
      }, {});

      setQueriesResultsMap(mappedObject);
    } catch (error: any) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Error fetching query data!',
        }),
      );
      console.error('error fetching data', error);
    } finally {
      setIsFetchingData(false);
    }
  };

  // handle search
  const setEntitiesManagerFilters = (input: string) => {
    if (!apiRef?.current || !apiRef?.current?.setFilterModel) return;

    const filterValues: string[] = parseSearchInput(input);

    if (filterValues.length === 0) {
      apiRef?.current?.setFilterModel({ items: [] });
      return;
    }

    // get first two searchable fields
    const searchableFields = apiRef.current
      .getAllColumns()
      .filter((col) => col.filterable !== false && col.field !== '__check__')
      .map((col) => col.field)
      .slice(0, 2);

    if (searchableFields.length === 0) return;

    // build filter items: value × field matrix
    const filterItems: any = [];

    filterValues.forEach((value, vi) => {
      searchableFields.forEach((field, fi) => {
        filterItems.push({
          id: `filter-${field}-${vi}-${fi}`,
          field: field,
          operator: 'contains',
          value,
        });
      });
    });

    // Step 4: Apply filter model
    apiRef.current.setFilterModel({
      items: filterItems,
      logicOperator: GridLogicOperator.Or,
    });
  };

  const handleSelectAll = () => {
    const filteredRows = gridFilteredSortedRowEntriesSelector(apiRef).map((entry) => entry.model);
    //@ts-ignore
    setValue(
      'selectedEntities',
      [
        ...filteredRows.map((entity: any) => augmentEntityWithClientName(entity, entityPrimaryKey)),
        ...selectedEntities,
      ],
      { shouldDirty: true, shouldTouch: true, shouldValidate: true },
    );
  };

  const debouncedSearch = useMemo(() => {
    return debounce(setEntitiesManagerFilters, config.defaultDebounce);
  }, [setEntitiesManagerFilters]);

  // clean up debounce on unmount
  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  // run debounce when `searchValue` changes
  useDeepCompareEffect(() => {
    debouncedSearch(searchValue);
  }, [searchValue, debouncedSearch]);

  useDeepCompareEffect(() => {
    if ((!selectedEntities || selectedEntities.length === 0) && queriesResultsMap) {
      setQueriesResultsMap(null);
    }
  }, [selectedEntities, queriesResultsMap]);

  useDeepCompareEffect(() => {
    handleCheckQueriesForSelectedEntities(selectedEntities, projectData);
  }, [selectedEntities, projectData]);

  useEffect(() => {
    if (!apiRef?.current || Object.keys(apiRef.current).length <= 0) return;

    const handleStateChange = () => {
      if (!apiRef?.current) return;

      const entries = gridFilteredSortedRowEntriesSelector(apiRef);
      setSourceFilteredRowCount(entries.length);
    };

    handleStateChange();

    const unsubscribe = apiRef.current.subscribeEvent?.('stateChange', handleStateChange);

    return () => {
      unsubscribe?.();
    };
  }, [apiRef?.current]);

  return (
    <ProjectPageLayout noWrapper>
      <StyledGrid>
        <FormProvider {...formMethods}>
          <NgCard>
            <NgCardContent>
              <NgCardHeader hideFilters>
                <NgBreadcrumbs data={pageBreadcrumbs} />
              </NgCardHeader>

              {!typesData || typesData?.length <= 0 ? (
                <NoDataMessage grants={grants} />
              ) : (
                <>
                  <br />

                  <h3>Selected</h3>

                  <StyledContainer>
                    <Controller
                      control={control}
                      name="selectedEntities"
                      render={({ field }) => (
                        <QueryOutputTable
                          rows={field.value}
                          isLoading={isLoading}
                          onChange={field.onChange}
                          schema={selectedEntitiesSchema}
                          entityPrimaryKey={entityPrimaryKey}
                        />
                      )}
                    />

                    <br />
                    <br />

                    {!isWorkspaceTemplateDataLoading && (
                      <StyledContainer>
                        <NgBreadcrumbs data={sourceBreadcrumbs} onClick={handleBreadcrumbClick} />

                        <StyledHeader>
                          <SearchInput
                            placeholder="Start typing a policy# or client name, or a list of policy numbers separated by comma"
                            value={searchValue}
                            onSearch={setSearchValue}
                            hideIcon
                          />

                          <NgButton
                            disabled={!!searchValue && sourceFilteredRowCount === 0}
                            variant={buttonVariants.clear}
                            testId="data-page-select-all-btn"
                            onClick={handleSelectAll}
                          >
                            Select all
                          </NgButton>
                        </StyledHeader>

                        <EntitiesManager
                          apiRef={apiRef}
                          typesData={typesData}
                          columnsByTypes={computedTypesData?.columnsByTypes}
                          entityPrimaryKey={entityPrimaryKey}
                          selectedType={selectedType}
                          isLoading={isLoading}
                          selectedEntities={selectedEntities}
                          onChange={handleEntitiesManagerChange}
                          onFilterModelChange={(model: any) => {
                            if (model?.items?.length === 0 && searchValue) {
                              setSearchValue('');
                            }
                          }}
                          cmsScopeId={projectData?.CmsScope?.ID}
                          urlPrefix="projects"
                        />
                      </StyledContainer>
                    )}
                  </StyledContainer>
                </>
              )}
            </NgCardContent>
          </NgCard>

          <div className="column">
            <GenerateDocumentsSection
              entityPrimaryKey={entityPrimaryKey}
              projectData={projectData}
              attributes={selectedEntities}
              queriesResultsMap={queriesResultsMap}
              workspaceTemplateData={workspaceTemplateData}
              isQueryValid={!!queriesResultsMap && selectedEntities?.length > 0}
              isLoading={isWorkspaceTemplateDataLoading}
              isQueriesEnabled={true}
            />

            <MicrositesPublishSection
              isLoading={isLoading}
              projectData={projectData}
              isQueryValid={!!queriesResultsMap && selectedEntities?.length > 0}
              isQueriesEnabled={true}
            />
          </div>
        </FormProvider>
      </StyledGrid>
    </ProjectPageLayout>
  );
};
